import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useGetUtilityDataByBucketQuery } from 'src/services/switch-rest-api/utility-data.service';
import StackedBarChart from 'switch-fe-shared/components/Charts/StackedBarChart';
import { UtilityTypeBadge } from 'switch-fe-shared/components/Chips/UtilityBadge';
import GenericDialog from 'switch-fe-shared/components/Dialogs/GenericDialog';
import { formatNumber, graphFileName } from 'switch-fe-shared/utils/formatting';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

interface Props {
  isOpen: boolean;
  handleDialogClose: () => void;
  notification: any;
}

const NotificationDialog = ({
  isOpen,
  handleDialogClose,
  notification,
}: Props) => {
  interface CalculateStartTimeEndTime {
    startTimeTemp: string;
    endTimeTemp: string;
  }

  const calculateStartTimeEndTime = ({
    startTimeTemp,
    endTimeTemp,
  }: CalculateStartTimeEndTime) => {
    const diff = dayjs(startTimeTemp).diff(endTimeTemp, 'day');

    const interval = 1440;
    const startTime = dayjs(startTimeTemp).startOf('day').toISOString();
    const endTime = dayjs(endTimeTemp).endOf('day').toISOString();

    // switch (true) {
    // case (diff === 0):
    //     interval = 3600
    //     startTime = dayjs(startTimeTemp).startOf('day').toISOString()
    //     endTime = dayjs(endTimeTemp).endOf('day').toISOString()
    //     break
    // default:
    //     interval = 86400
    //     startTime = dayjs(startTimeTemp).startOf('day').toISOString()
    //     endTime = dayjs(endTimeTemp).endOf('day').toISOString()
    //     break
    // }

    return { interval, startTime, endTime };
  };

  const params = calculateStartTimeEndTime({
    startTimeTemp: notification.startTime,
    endTimeTemp: notification.endTime,
  });

  const {
    isLoading: isUtilityDataLoading,
    data: utilityData,
    isSuccess: isUtilityDataSuccess,
    isFetching: isUtilityDataFetching,
    isError,
    refetch,
  } = useGetUtilityDataByBucketQuery(
    {
      meterIds: [notification.meterId],
      startTimeISO: params.startTime,
      endTimeISO: params.endTime,
      bucketSize: params.interval,
      bucketUnit: DurationUnit.Minutes,
    },
    { skip: !notification.meterId }
  );

  const series: ApexAxisChartSeries = useMemo(
    () => [
      {
        name: 'Usage',
        group: 'Usage',
        data: (utilityData ? utilityData.buckets : []).map(
          (ele) => ele.deltaP1 / 1000
        ),
      },
    ],
    [utilityData]
  );

  const customContent = (
    <Card>
      <CardHeader
        avatar={UtilityTypeBadge({
          type: notification.utilityType,
        })}
        title='Notification Details'
        subheader={`${notification.notificationType.toUpperCase()} at ${
          notification.applicationName
        } for meter: ${notification.meterName} | FROM ${dayjs(
          notification.startTime
        ).format('YYYY-MM-DD HH:mm')} TO ${dayjs(notification.endTime).format(
          'YYYY-MM-DD HH:mm'
        )}`}
      />
      <CardContent>
        {isUtilityDataLoading ? (
          <div
            style={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </div>
        ) : utilityData?.buckets.length !== 0 ? (
          <Grid container>
            <Grid item xs={12} height={350}>
              <StackedBarChart
                key={'notification-chart'}
                id={graphFileName(
                  `notification-chart`,
                  notification.startTime,
                  notification.endTime
                )}
                group='usage'
                series={series}
                customOptions={(options) => {
                  return {
                    ...options,
                    xaxis: {
                      ...options.xaxis,
                      type: 'datetime',
                    },
                    yaxis: [
                      {
                        title: {
                          text: `Usage`,
                        },
                        labels: {
                          formatter: (val) =>
                            val != null
                              ? `${formatNumber(Math.abs(val))}`
                              : 'N/A',
                        },
                      },
                    ],
                  };
                }}
                categories={
                  utilityData?.buckets
                    ? utilityData.buckets.map((bucket) => bucket.timestampISO)
                    : []
                }
                isLoading={isUtilityDataLoading || isUtilityDataFetching}
              />
            </Grid>
          </Grid>
        ) : (
          <div
            style={{
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant='h5' align='center'>
              There is no data associated with this meter or something went
              wrong
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <GenericDialog
      open={isOpen}
      title='Notification Details'
      onCancel={handleDialogClose}
      cancelButtonText='Close'
      customContent={customContent}
      maxWidth='lg'
    />
  );
};

export default NotificationDialog;
